import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  InputAdornment,
  Paper,
  Divider,
  Box,
  IconButton,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import * as yup from "yup";
import { useFormik } from "formik";
import { connect } from "react-redux";
import {
  START_PHARMACY_CREATE_PASSWORD,
  RESET_STATE,
} from "Actions/AuthAction";
import { ReactComponent as LockIcon } from "Assets/Icons/lockIcon.svg";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useNotification } from "Hooks/NotificationManager";
import Button from "Components/Common/Button/Button";
import "../../Sass/Auth.scss";
import regex from "constants/regex";
import PrimaryIcon from "Components/Common/PrimaryIcon";

const useStyle = makeStyles((theme) => ({
  rsButton: {
    marginTop: "30px !important",
    "&>button": {
      display: "flex",
      margin: "auto",
      padding: "8px 40px !important",
    },
  },
}));

const CreatePharmacyPassword = (props) => {
  const {
    dispatchCreatePharmacyPassword,
    createPharmacyPasswordDetails,
    dispatchResetState,
  } = props;
  const classes = useStyle();
  const navigate = useNavigate();
  const notification = useNotification();
  const params = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showCPassword, setShowCPassword] = useState(false);
  const [isCreatePharmacyPasswordProceed, setIsCreatePharmacyPasswordProceed] =
    useState(false);

  /** Handle Create Pharmacy Password */
  const handleCreatePharmacyPassword = (data) => {
    setIsCreatePharmacyPasswordProceed(true);
    dispatchCreatePharmacyPassword({
      password: data.password,
      token: params?.token,
    });
  };

  /** Create Pharmacy Password Schema */
  const createPharmacyPasswordSchema = yup.object().shape({
    password: yup
      .string("Must be string")
      .required("Password is required field.")
      .min(8, "Password length should be atleast 8 characters.")
      .matches(
        /^(?=.*[a-z])/,
        "password must contain at least 1 lower case letter"
      )
      .matches(
        /^(?=.*[A-Z])/,
        "password must contain at least 1 upper case letter"
      )
      .matches(/^(?=.*[0-9])/, "password must contain at least 1 number")
      .matches(
        regex.password,
        "password must contain at least 1 special character"
      ),
    confirm_password: yup
      .string("Must be string")
      .required("Confirm Password is required field.")
      .matches(
        /^(?=.*[a-z])/,
        "Confirm password must contain at least 1 lower case letter"
      )
      .matches(
        /^(?=.*[A-Z])/,
        "Confirm password must contain at least 1 upper case letter"
      )
      .matches(
        regex.password,
        "Confirm password must contain at least 1 number"
      )
      .matches(
        /^(?=.*[0-9])/,
        "Confirm password must contain at least 1 special character"
      )
      .oneOf(
        [yup.ref("password"), null],
        "Confirm password should be same as password"
      ),
  });

  /** Formik */
  const {
    handleSubmit,
    handleChange,
    values,
    errors,
    touched,
    resetForm,
    setFieldError,
    handleBlur,
  } = useFormik({
    initialValues: {
      password: "",
      confirm_password: "",
    },
    validationSchema: createPharmacyPasswordSchema,
    onSubmit: handleCreatePharmacyPassword,
    enableReinitialize: true,
  });

  /* Loading UseEffect  */
  useEffect(() => {
    if (isCreatePharmacyPasswordProceed) {
      if (!createPharmacyPasswordDetails.success) {
        if (createPharmacyPasswordDetails.errors) {
          const errorPresent =
            Object.keys(createPharmacyPasswordDetails.errors).length > 0;
          if (errorPresent > 0) {
            Object.keys(createPharmacyPasswordDetails.errors).forEach(function (
              key
            ) {
              setFieldError(key, createPharmacyPasswordDetails.errors[key][0]);
            });
          }
        }
      } else {
        notification.success("Password has been updated successfully.");
        resetForm();
        dispatchResetState("createPharmacyPasswordDetails", "");
        navigate("/home");
      }
      setIsCreatePharmacyPasswordProceed(false);
    }
  }, [
    createPharmacyPasswordDetails.success,
    createPharmacyPasswordDetails.errors,
  ]);

  return (
    <Paper className="rsRoot">
      <div elevation={0} className="rsBoxsubroot">
        <Typography variant="h1" bold>
          Repeatsmart
        </Typography>
        <Typography variant="h2" semiBold>
          Create Password
        </Typography>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className="rsForm"
        >
          <TextField
            secondary
            fullWidth
            name="password"
            placeholder="New Password"
            autoComplete="password"
            autoFocus
            type={showPassword ? "text" : "password"}
            value={values.password}
            onBlur={handleBlur}
            onChange={(e) => {
              handleChange(e);
            }}
            InputProps={{
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <PrimaryIcon width="20px" icon={LockIcon} />
                  </InputAdornment>
                  <Divider className="rsInputDivider" />
                </>
              ),
              endAdornment: (
                <>
                  <InputAdornment position="start">
                    <IconButton
                      aria-label="toggle password visibility"
                      edge="end"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }}
            error={touched.password && errors.password}
            helperText={touched.password && errors.password}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            secondary
            fullWidth
            name="confirm_password"
            type={showCPassword ? "text" : "password"}
            placeholder="Confirm Password"
            autoComplete="confirm_password"
            value={values.confirm_password}
            onBlur={handleBlur}
            onChange={(e) => {
              handleChange(e);
            }}
            InputProps={{
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <PrimaryIcon width="20px" icon={LockIcon} />
                  </InputAdornment>
                  <Divider className="rsInputDivider" />
                </>
              ),
              endAdornment: (
                <>
                  <InputAdornment position="start">
                    <IconButton
                      aria-label="toggle password visibility"
                      edge="end"
                      onClick={() => setShowCPassword(!showCPassword)}
                    >
                      {showCPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                </>
              ),
            }}
            error={touched.confirm_password && errors.confirm_password}
            helperText={touched.confirm_password && errors.confirm_password}
            InputLabelProps={{ shrink: true }}
          />
          <Box className={classes.rsButton}>
            <Button
              children="Submit"
              variant="contained"
              color="primary"
              disabled={createPharmacyPasswordDetails?.isLoading}
              loading={createPharmacyPasswordDetails?.isLoading}
              type="submit"
            />
          </Box>
        </form>
      </div>
    </Paper>
  );
};

const mapStateToProps = (state) => ({
  createPharmacyPasswordDetails: state.auth.createPharmacyPasswordDetails,
});
const mapDispatchToProps = (dispatch) => ({
  dispatchCreatePharmacyPassword: (data, token) =>
    dispatch({ type: START_PHARMACY_CREATE_PASSWORD, data, token }),
  dispatchResetState: (key, payload) =>
    dispatch({ type: RESET_STATE, key, payload }),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CreatePharmacyPassword);
